import React, { useState } from 'react';
import { CheckCircle2Icon, MapPinIcon, NavigationIcon, TruckIcon } from 'lucide-react';
import { toast } from 'sonner';
import type { DemoUser } from '../../types/config';
import type { FieldScreen } from '../../types/navigation';
import { useStore } from '../../contexts/StoreContext';
import { MobileHeader } from '../../components/mobile/MobileHeader';
import { CaseMiniCard } from '../../components/mobile/CaseMiniCard';
import { DhakaMap } from '../../components/shared/DhakaMap';
import { fmtDateTime, nowIso } from '../../utils/bn';
export function PickupScreen({
  pcr,
  user,
  go
}: {
  pcr?: string;
  user: DemoUser;
  go: (s: FieldScreen) => void;
}) {
  const {
    getCase,
    cases,
    config,
    updateCase
  } = useStore();
  const [done, setDone] = useState(false);
  const c = pcr ? getCase(pcr) : undefined;
  if (!c) {
    const list = cases.filter(x => x.stage === 'registered' || x.stage === 'in_transit');
    return <>
        <MobileHeader appName="ডিএমপি যানবাহন হেফাজত" title="নির্ধারিত পিকআপ" onBack={() => go({
        name: 'home'
      })} data-id="element-1447" data-mp="ekjn93b5p7bt" />
        <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-2" data-id="element-1448" data-mp="2xxr9ydy60e94">
          <DhakaMap className="h-48" markers={list.map(x => ({
          id: x.pcr,
          x: x.location.x,
          y: x.location.y,
          label: x.pcr,
          kind: x.stage === 'registered' ? 'pin' : 'custody'
        }))} onSelect={id => go({
          name: 'pickup',
          pcr: id
        })} data-id="element-1449" data-mp="2sywjcptyhleg" />
          {list.length === 0 && <p className="py-8 text-center text-sm text-ink-muted" data-id="element-1450" data-mp="3gtz7swoulmue">কোনো নির্ধারিত পিকআপ নেই।</p>}
          {list.map(x => <CaseMiniCard key={x.pcr} c={x} onClick={() => go({
          name: 'pickup',
          pcr: x.pcr
        })} extra={<span className="text-[11px] text-ink-soft" data-id="element-1452" data-mp="3pncd6y43mgxd">{x.road}</span>} data-id="element-1451" data-mp="3bumk69q5kbqr" />)}
        </div>
      </>;
  }
  const station = config.stations.find(s => s.name === c.assignedStation);
  const canPickup = c.stage === 'registered' && c.barcode.attached;
  const complete = () => {
    updateCase(c.pcr, x => ({
      ...x,
      stage: 'in_transit'
    }), {
      key: 'pickup',
      actor: user.name,
      role: user.title,
      action: `পিকআপ সম্পন্ন — ${c.assignedStation}-এর পথে পরিবহনাধীন`
    });
    setDone(true);
    toast.success('পিকআপ সম্পন্ন। কেসের অবস্থা: পরিবহনাধীন।');
  };
  return <>
      <MobileHeader appName="ডিএমপি যানবাহন হেফাজত" title="পিকআপ নিশ্চিতকরণ" onBack={() => go({
      name: 'pickup'
    })} data-id="element-1453" data-mp="319pi2llvi93o" />
      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-3" data-id="element-1454" data-mp="360pb3frvg70w">
        {(done || c.stage === 'in_transit') && <div className="flex items-center gap-3 rounded-xl border border-success/20 bg-success-50 p-3" data-id="element-1455" data-mp="3ayqw468uygf0">
            <CheckCircle2Icon className="h-7 w-7 text-success" data-id="element-1456" data-mp="3fwinmz5lc0io" />
            <div data-id="element-1457" data-mp="3jzp40zmf382v"><p className="text-sm font-bold text-success" data-id="element-1458" data-mp="3k1070x81gbsx">পরিবহনাধীন</p><p className="text-xs text-ink-muted" data-id="element-1459" data-mp="3oz1ojdptg32a">স্টেশন কর্মকর্তা বারকোড স্ক্যান করে গ্রহণ করবেন।</p></div>
          </div>}
        <div className="card overflow-hidden" data-id="element-1460" data-mp="1pmpiyqz9plo7">
          <img src={c.photo} alt={`${c.regNo} যানবাহনের ছবি`} className="h-40 w-full object-cover" data-id="element-1461" data-mp="1po0lyokw2pe9" />
          <dl className="divide-y divide-line text-sm" data-id="element-1462" data-mp="1un3g3dzbzb7g">
            {[['PCR নম্বর', c.pcr], ['যানবাহন', c.regNo], ['আটক স্থান', c.road], ['নির্ধারিত রেকার', c.wreckerId], ['অপারেটর', c.operator], ['পিকআপ তারিখ/সময়', fmtDateTime(nowIso())], ['গন্তব্য ডাম্পিং স্টেশন', c.assignedStation], ['বারকোড স্টিকার', c.barcode.attached ? `${c.barcode.serial} — সংযুক্ত` : 'সংযুক্ত হয়নি']].map(([k, v]) => <div key={k} className="flex justify-between gap-3 px-4 py-2" data-id="element-1463" data-mp="211irytot0lnz"><dt className="text-ink-muted" data-id="element-1464" data-mp="2122762n2kvyb">{k}</dt><dd className="text-right font-semibold" data-id="element-1465" data-mp="1w1wf4lcw17fp">{v}</dd></div>)}
          </dl>
        </div>
        <DhakaMap className="h-44" markers={[{
        id: 'from',
        x: c.location.x,
        y: c.location.y,
        label: c.location.label,
        kind: 'pin'
      }, ...(station ? [{
        id: 'to',
        x: station.location.x,
        y: station.location.y,
        label: station.name,
        kind: 'station' as const
      }] : [])]} data-id="element-1466" data-mp="1s143xmkggsgs" />
        <p className="flex items-center gap-1.5 text-xs text-ink-muted" data-id="element-1467" data-mp="24hlojoizaer9"><MapPinIcon className="h-3.5 w-3.5 text-danger" data-id="element-1468" data-mp="24g0tlxsr4tsv" /> আটক স্থান <NavigationIcon className="ml-2 h-3.5 w-3.5 text-bdgreen" data-id="element-1469" data-mp="299vidfn7ryyt" /> গন্তব্য স্টেশন (রেকার ট্র্যাকিং সিমুলেটেড)</p>
        {!c.barcode.attached && c.stage === 'registered' && <div className="rounded-lg border border-warning/40 bg-warning-50 p-3 text-xs text-warning-700" data-id="element-1470" data-mp="1zihc8p0rc0wp">
            পিকআপের আগে বারকোড স্টিকার প্রিন্ট ও সংযুক্ত করতে হবে।
            <button className="btn btn-sm btn-warning mt-2 w-full" onClick={() => go({
          name: 'sticker',
          pcr: c.pcr
        })} data-id="element-1471" data-mp="2s7ob5p44isih">বারকোড প্রিন্ট করুন</button>
          </div>}
      </div>
      <div className="shrink-0 border-t border-line bg-white p-3" data-id="element-1472" data-mp="2e9zr2jhvrpeh">
        {done || c.stage !== 'registered' ? <button className="btn btn-outline btn-lg w-full" onClick={() => go({
        name: 'home'
      })} data-id="element-1473" data-mp="12srom3qsojcy">হোমে ফিরুন</button> : <button className="btn btn-green btn-lg w-full" disabled={!canPickup} onClick={complete} data-id="element-1474" data-mp="16ulk7xm4ux69"><TruckIcon className="h-5 w-5" data-id="element-1475" data-mp="zaay7vgyq3xd" /> পিকআপ সম্পন্ন করুন</button>}
      </div>
    </>;
}